import { useSelector, useDispatch } from "react-redux";
import { Dialog } from "primereact/dialog";
import { toggleDialog } from "../../../../redux/Slices/Global/globalSlice";
import { useEffect, useState, useCallback, useRef } from "react";
import { useSendActionDevice } from "../../../../queries";
import { ProgressSpinner } from "primereact/progressspinner";
import { useTranslation } from "react-i18next";
import {
    getScreenshotBaseline,
    pollForNewScreenshot,
    fetchExistingScreenshot,
} from "../../../../utils/screenshot";

const IcCamera = () => <svg viewBox="0 0 24 24"><rect x="3" y="6" width="18" height="14" rx="2" /><circle cx="12" cy="13" r="3.5" /><path d="M8 6l1.5-2h5L16 6" /></svg>;
const IcShotPlaceholder = () => <svg viewBox="0 0 24 24"><rect x="3" y="5" width="18" height="13" rx="2" /><path d="M8 21h8" /></svg>;
const IcClose = () => <svg width="18" height="18" viewBox="0 0 24 24" style={{ stroke: 'currentColor', fill: 'none', strokeWidth: 2 }}><path d="M6 6l12 12M18 6L6 18" /></svg>;

function ScreenshotPreviewer() {
    const { t } = useTranslation();
    const { openDialog } = useSelector((state) => state.global);
    const { selectedDevice } = useSelector((state) => state.device);
    const dispatch = useDispatch();

    const [imageUrl, setImageUrl] = useState(null);
    const [imageInfo, setImageInfo] = useState();

    const [error, setError] = useState(null);
    const [imgLoading, setImgLoading] = useState(true);

    const deviceAction = useSendActionDevice();

    const onClose = () => {
        dispatch(toggleDialog("screenshot"));
    };

    // Show whatever screenshot already exists (no waiting).
    const showExisting = useCallback(async () => {
        const existing = await fetchExistingScreenshot(selectedDevice?.serial_name);
        if (existing) {
            setImageUrl(existing.url);
            setImageInfo(existing.metadata);
            setError(null);
        } else {
            setImageUrl(null);
            setError(t("devices.preview_no_screenshot"));
        }
    }, [selectedDevice, t]);

    // Capture a FRESH screenshot: record the current upload time, send the
    // action, then poll Storage until a strictly-newer image lands and show it.
    // (Fetching once after a fixed delay would often miss the upload, or return
    // the stale image — which read as "nothing happens".)
    const screenShot = useCallback(async (deviceID, action) => {
        const serial = selectedDevice?.serial_name;
        setImgLoading(true);
        setError(null);
        try {
            const baseline = await getScreenshotBaseline(serial);
            await deviceAction.mutateAsync({
                id: deviceID,
                data: { action: action.toLowerCase() },
            });
            const result = await pollForNewScreenshot(serial, baseline);
            if (result) {
                setImageUrl(result.url);
                setImageInfo(result.metadata);
            } else {
                await showExisting(); // didn't upload in time — show last known
            }
        } catch (err) {
            console.error("Screenshot action failed", err);
            setError(t("devices.preview_screenshot_action_failed"));
        } finally {
            setImgLoading(false);
        }
    }, [deviceAction, selectedDevice, showExisting, t]);

    // The device-card menu opens this dialog for a device; auto-capture a fresh
    // screenshot once per open so the user sees the live screen (the card just
    // selects the device + opens the dialog — the capture happens here).
    const capturedForOpenRef = useRef(false);
    useEffect(() => {
        if (openDialog.screenshot && selectedDevice?.id && !capturedForOpenRef.current) {
            capturedForOpenRef.current = true;
            screenShot(selectedDevice.id, "screenshot");
        } else if (!openDialog.screenshot) {
            capturedForOpenRef.current = false; // reset for the next open
        }
    }, [openDialog.screenshot, selectedDevice, screenShot]);

    return (
        <Dialog
            visible={openDialog.screenshot}
            onHide={onClose}
            draggable={false}
            breakpoints={{ "960px": "95vw" }}
            style={{ width: "460px" }}
            showHeader={false}
            className="pp-dlg"
        >
            <div className="pp-dlg-hd">
                <span className="ic"><IcCamera /></span>
                <div>
                    <div className="t">{t("devices.group_screenshot")} — {selectedDevice?.name}</div>
                    {imageInfo && <div className="s">{t("devices.preview_uploaded")}: {new Date(imageInfo.timeCreated).toLocaleString()}</div>}
                </div>
                <span className="x" onClick={onClose}><IcClose /></span>
            </div>

            <div className="pp-dlg-bd">
                <div className="pp-dlg-shot">
                    {imgLoading ? (
                        <ProgressSpinner style={{ width: '42px', height: '42px' }} strokeWidth="3" animationDuration="2s" />
                    ) : imageUrl ? (
                        <img src={imageUrl} alt={t("devices.preview_device_screenshot")} />
                    ) : (
                        <>
                            <IcShotPlaceholder />
                            <span>{error || t("devices.preview_no_screenshot")}</span>
                        </>
                    )}
                </div>
                {imageInfo && (
                    <div className="meta mt-2">
                        {t("devices.preview_size")}: {(imageInfo.size / 1024).toFixed(2)} KB · {t("devices.preview_type")}: {imageInfo.contentType}
                    </div>
                )}
            </div>

            <div className="pp-dlg-ft">
                <button className="btn primary" disabled={deviceAction.isLoading || imgLoading} onClick={() => screenShot(selectedDevice.id, "screenshot")}>
                    {t("devices.preview_recapture")}
                </button>
                <button className="btn ghost" onClick={onClose}>{t("buttons.close")}</button>
            </div>
        </Dialog>
    );
}

export default ScreenshotPreviewer;
